import { Injectable } from '@angular/core';

import { logError } from '@com-delta-idp/mnx-utils';
import { Observable, Subject, merge, of, timer } from 'rxjs';
import {
  catchError,
  shareReplay,
  switchMap,
  takeUntil,
  takeWhile,
  tap,
  withLatestFrom
} from 'rxjs/operators';
import {
  FlyDeltaPoll,
  LoginOAuthParams,
  MethodSelectionPayload
} from '../../../models';
import { FlyDeltaPollService } from '../../multi-factor-authentication-adapter-service/fly-delta-poll/fly-delta-poll.service';
import { SubmitFlyDeltaMethodSelectionService } from '../../multi-factor-authentication-adapter-service/submit-method-selection/submit-fly-delta-selection/submit-fly-delta-method-selection.service';
import { MfaUtilService } from '../../multi-factor-authentication-adapter-service/util-service/mfa-util.service';

@Injectable({
  providedIn: 'root'
})
export class FlyDeltaMethodService {
  cancelPairing$ = new Subject();
  authCode = '';

  constructor(
    private flyDeltaMethod: SubmitFlyDeltaMethodSelectionService,
    private flyDeltaPoll: FlyDeltaPollService,
    private mfaUtils: MfaUtilService
  ) {}

  initFlyDelta(
    loginOAuthParams: LoginOAuthParams,
    flowdId: string,
    applicationName: string,
    applicationId: string
  ): Observable<string | FlyDeltaPoll> {
    const payload: MethodSelectionPayload = {
      devicePairingMethod: {
        deviceType: 'MOBILE',
        applicationName: applicationName,
        applicationId: applicationId
      }
    };

    const pollTime =
      loginOAuthParams.oauthConfig.FLY_DELTA_POLLING_TIMER ?? 5000;

    const init$ = this.flyDeltaMethod
      .submitFlyDeltaMethodSelection(
        loginOAuthParams.oauthConfig,
        flowdId,
        payload
      )
      .pipe(shareReplay());

    const poll$ = timer(0, pollTime).pipe(
      withLatestFrom(init$),
      switchMap(([, res]) =>
        this.flyDeltaPoll.submitFlyDeltaPoll(
          loginOAuthParams.oauthConfig,
          res.id ? res.id : ''
        )
      ),
      takeUntil(this.cancelPairing$),
      takeWhile((val) => val.status === 'MOBILE_ACTIVATION_REQUIRED', true)
    );

    return merge(init$, poll$).pipe(
      tap((res: FlyDeltaPoll) => {
        if (res.status === 'COMPLETED') {
          this.authCode = res.authorizeResponse?.code ?? '';
        }
      }),
      catchError((error) => {
        logError(
          error as Error,
          'MULTI_FACTOR_AUTHENTICATION',
          'PROFILE',
          'FlyDeltaPoll'
        );
        return of(this.mfaUtils.getMfaRegistrationError(error));
      })
    );
  }
}
